import React, {useEffect, useRef, useState} from "react";

import {EditOutlined} from "@ant-design/icons";
import {Button, Checkbox, Col, DatePicker, Descriptions, Input, InputNumber, message, Row, Select, Spin} from "antd";
import {useForm} from "antd/es/form/Form";
import dayjs from "dayjs";

import {ForecastingGraph} from "./ForecastingGraph";
import api from "../../api";
import {DEFAULT_FORECAST_END, ROP_MIN_LABEL} from "../../properties";
import {Recommendations, FieldsNaming} from "../../variants";
import {StandardForm} from "../helpers/forms/StandardForm";
import {ModalWindow} from "../helpers/modals/ModalWindow";
import {useClickOutside} from "../helpers/UseClickOutside";

function Edit({onSave, onCancel, defaultValue, _type, options = []}) {
	const [_value, setValue] = useState();
	const [dateValue, setDateValue] = useState();
	const wrapperRef = useRef(null);

	const workWithDate = (date, dateString) => {
		setValue(dateString);
		setDateValue(date);
	};

	useClickOutside(wrapperRef, onCancel, true);

	const getInput = () => {
		if (_type === "numeric") {
			return (
				<InputNumber
					autoFocus={true}
					defaultValue={defaultValue}
					value={_value}
					onChange={event => {
						setValue(event);
					}}
				/>
			);
		}

		if (_type === "date") {
			return (
				<DatePicker
					defaultPickerValue={dayjs(defaultValue, "YYYY-MM")}
					defaultValue={dayjs(defaultValue, "YYYY-MM")}
					onChange={workWithDate}
					value={dateValue}
					picker="month"
				/>
			);
		}

		if (_type === "checkbox") {
			return (
				<Checkbox
					style={{marginRight: 10}}
					defaultChecked={defaultValue}
					value={_value}
					onChange={event => setValue(event.target.checked)}
				/>
			);
		}

		if (_type === "select") {
			return (
				<Select
					autoFocus={true}
					defaultOpen={true}
					style={{marginRight: 10, width: 150}}
					defaultValue={defaultValue}
					value={_value}
					onChange={value => setValue(value)}
					options={options}
				/>
			);
		}

		if (_type === "tag-select") {
			return (
				<Select
					autoFocus={true}
					defaultOpen={true}
					style={{marginRight: 10, width: 150}}
					mode={"tags"}
					defaultValue={defaultValue}
					value={_value}
					onChange={value => setValue(value)}
					options={options}
				/>
			);
		}
		return (
			<Input
				autoFocus={true}
				defaultValue={defaultValue}
				value={_value}
				onChange={event => setValue(event.target.value)}
			/>
		);
	};

	return (
		<div ref={wrapperRef}>
			{getInput()}
			<Button
				onClick={() => {
					onSave(_value);
				}}
				type="primary">
				Submit
			</Button>
		</div>
	);
}

export function SimpleForecasting({
	projectId,
	material,
	forecastMethod,
	itemInfo,
	forecastMin,
	forecastMax,
	canEdit,
	onRecAdd,
	change,
	currentRec,
	constants,
}) {
	const [min, setMin] = useState(itemInfo["rop"]);
	const [max, setMax] = useState(itemInfo["max"]);
	const [holding, setHolding] = useState(constants.holding);
	const [end, setEnd] = useState(DEFAULT_FORECAST_END);
	const [std_coeff, setStdCoeff] = useState(constants.std_coeff);
	const [smoothingMethod, setSmoothingMethod] = useState(constants.smoothing_mode);
	const [useSmoothing, setUseSmoothing] = useState(constants.use_demand_smoothing);

	const [info, setInfo] = useState({});
	const [leadTime, setLeadTime] = useState(info?.lead_time_days);

	useEffect(() => {
		if (info?.lead_time_days != null) {
			setLeadTime(info.lead_time_days);
		}
	}, [info]);

	const [usePm, setUsePm] = useState("corrective");

	const [title, setTitle] = useState(currentRec);

	const [isModalVisible, setIsModalVisible] = useState(false);

	const [editingMode, setEditingMode] = useState(false);
	const [editField, setEditField] = useState("");

	const [graph, setGraph] = useState([]);
	const [plannedGraph, setPlannedGraph] = useState(null);

	const [loading, setLoading] = useState(false);

	const [form] = useForm();

	const [sdcCodes, setSdcCodes] = useState([]);
	const [skipSDC, setSkipSDC] = useState([]);

	const save = () => {
		form.submit();
	};

	useEffect(() => {
		setTitle(currentRec);
	}, [currentRec]);

	const getForecast = (
		_min = min,
		_max = max,
		_holding = holding,
		_end = end,
		useForecast = false,
		_usePm = usePm,
		_skipSDC = skipSDC,
		_use_smoothing = useSmoothing,
		_std_coeff = std_coeff,
		_smoothing_method = smoothingMethod,
		_leadTime = leadTime,
	) => {
		setLoading(true);
		if (_min === "" || _max === "") {
			_min = itemInfo["rop"];
			_max = itemInfo["max"];
		}

		if (useForecast) {
			_min = forecastMin ? forecastMin : forecastMin === 0 ? forecastMin : _min;
			_max = forecastMax ? forecastMax : forecastMax === 0 ? forecastMax : _max;
		}

		if (_min !== "" && _max !== "") {
			const data = {
				min: _min,
				max: _max,
				holding: _holding / 100,
				end: _end,
				use_pm: _usePm,
				skip_sdc: _skipSDC,
				useSmoothing: _use_smoothing,
				std_coeff: _std_coeff,
				smoothing_mode: _smoothing_method,
				lead_time_days: _leadTime,
			};
			api.post(`/projects/${projectId}/material-analysis/${material}/forecast/${forecastMethod}`, data)
				.then(json => {
					setGraph(json.data.graph);
					setPlannedGraph(json.data.planned_graph);
					setInfo(json.data.info);
					setMin(_min);
					setMax(_max);
					setHolding(_holding);
					setEnd(json.data.info["actual_end_date"]);
					setUsePm(_usePm);
					setSkipSDC(_skipSDC);
					setUseSmoothing(_use_smoothing);
					setStdCoeff(_std_coeff);
					setSmoothingMethod(_smoothing_method);
				})
				.finally(() => setLoading(false));
		}
	};

	const canSaveUser = () => canEdit;

	useEffect(() => {
		getForecast(
			min,
			max,
			constants["holding"],
			end,
			true,
			constants["use_pm"],
			constants["skip_sdc"],
			constants["use_demand_smoothing"],
			constants["std_coeff"],
			constants["smoothing_mode"],
			null,
		);
		setTitle(currentRec);
		if (Object.keys(itemInfo).includes("sdc_code")) {
			setSdcCodes(
				itemInfo["sdc_code"].map(v => ({
					label: v,
					value: v,
				})),
			);
		}
	}, [itemInfo, forecastMax, forecastMin, change, constants, forecastMethod]);

	const startEdit = field => {
		setEditField(field);
		setEditingMode(true);
	};

	const finishEdit = () => {
		setEditField("");
		setEditingMode(false);
		setTitle("[User] User recommendation");
	};

	const getValuesFields = () => [
		{label: "Recommendation Name", name: "recommendation_name", _type: "text", required: true},
	];

	const onOk = values => {
		const userRec = {
			material: itemInfo["material"],
			name: values.recommendation_name,
			type: "User",
			raw: "",
			apply: "",
			min: min,
			max: max,
		};

		api.post(`/projects/${projectId}/material-analysis/${material}/user-recommendations`, userRec).then(json => {
			message.success("User Recommendations Successfully");
			setIsModalVisible(false);
		});

		onRecAdd();
	};

	const selectOptions = [
		{
			value: "all",
			label: "All",
		},
		{
			value: "corrective",
			label: "Corrective",
		},
		{
			value: "planned",
			label: "Planned",
		},
	];

	const selectSmoothingOptions = [
		{
			value: true,
			label: "Yes",
		},
		{
			value: false,
			label: "No",
		},
	];

	const labels = {
		exclude: "Remove spikes",
		limit: "Limit spikes",
	};

	const selectSmoothingMethodOptions = [
		{
			value: "exclude",
			label: "Remove spikes",
		},
		{
			value: "limit",
			label: "Limit spikes",
		},
	];

	return (
		<div>
			<div style={{textAlign: "center"}}>
				<div style={{fontWeight: "bold"}}>Selected Recommendation: </div>
				<div>{title}</div>
			</div>
			<Spin spinning={loading}>
				<Row>
					<Col span={10}>
						<Descriptions size={"small"} title="Material Data" column={2} bordered>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Current SoH"} span={1}>
								{info["stock_level"]}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"EOQ"} span={1}>
								{info["eoq"]}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={FieldsNaming.lead_time_days_name} span={1}>
								{info["lead_time_days"]}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={FieldsNaming.lead_time_months_name} span={1}>
								{info["lead_time_months"]}
							</Descriptions.Item>

							<Descriptions.Item
								labelStyle={{fontWeight: "bold"}}
								label={`Current ${ROP_MIN_LABEL}`}
								span={1}>
								{info["old_rop"]}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Current Max"} span={1}>
								{info["old_max"]}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Current SF"} span={1}>
								{info["service_factor"]} %
							</Descriptions.Item>

							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Target SF"} span={1}>
								{constants["service_level"]} %
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Safety Stock"} span={1}>
								{info["safety_stock"]}
							</Descriptions.Item>
						</Descriptions>
						{canSaveUser() && (
							<div
								style={{
									paddingTop: "10px",
									paddingBottom: "10px",
								}}>
								<Button
									type={"primary"}
									style={{
										float: "right",
									}}
									onClick={() => setIsModalVisible(true)}>
									Save as a recommendation
								</Button>
							</div>
						)}
						<Descriptions size={"small"} title="Model Parameters" bordered>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Proposed Max"} span={3}>
								{(!editingMode || editField !== "max") && (
									<div>
										{info["new_max"]}
										<EditOutlined onClick={() => startEdit("max")} />
									</div>
								)}
								{editingMode && editField === "max" && (
									<Edit
										defaultValue={info["new_max"]}
										_type={"numeric"}
										onCancel={finishEdit}
										onSave={value => {
											setMax(value);
											finishEdit();
											getForecast(min, value, holding, end);
										}}
									/>
								)}
							</Descriptions.Item>
							<Descriptions.Item
								labelStyle={{fontWeight: "bold"}}
								label={`Proposed ${ROP_MIN_LABEL}`}
								span={3}>
								{(!editingMode || editField !== "rop") && (
									<div>
										{info["new_rop"]} <EditOutlined onClick={() => startEdit("rop")} />
									</div>
								)}
								{editingMode && editField === "rop" && (
									<Edit
										defaultValue={info["new_rop"]}
										_type={"numeric"}
										onCancel={finishEdit}
										onSave={value => {
											setMin(value);
											finishEdit();
											getForecast(value, max, holding, end);
										}}
									/>
								)}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Holding Cost (%)"} span={3}>
								{(!editingMode || editField !== "holding") && (
									<div>
										{holding}
										<EditOutlined onClick={() => startEdit("holding")} />
									</div>
								)}
								{editingMode && editField === "holding" && (
									<Edit
										defaultValue={holding}
										_type={"numeric"}
										onCancel={finishEdit}
										onSave={value => {
											setHolding(value);
											finishEdit();
											getForecast(min, max, value, end);
										}}
									/>
								)}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={FieldsNaming.lead_time_days_name} span={3}>
								{(!editingMode || editField !== "lead_time") && (
									<div>
										{leadTime}
										<EditOutlined onClick={() => startEdit("lead_time")} />
									</div>
								)}
								{editingMode && editField === "lead_time" && (
									<Edit
										defaultValue={leadTime}
										_type={"numeric"}
										onCancel={finishEdit}
										onSave={value => {
											setLeadTime(value);
											finishEdit();
											getForecast(min, max, holding, end, false, usePm, skipSDC, useSmoothing, std_coeff, smoothingMethod, value);
										}}
									/>
								)}
							</Descriptions.Item>
							<Descriptions.Item
								labelStyle={{fontWeight: "bold"}}
								label={"Demand forecast data"}
								span={3}>
								{(!editingMode || editField !== "use_pm") && (
									<div>
										{usePm}
										<EditOutlined onClick={() => startEdit("use_pm")} />
									</div>
								)}
								{editingMode && editField === "use_pm" && (
									<Edit
										options={selectOptions}
										defaultValue={usePm}
										_type={"select"}
										onCancel={finishEdit}
										onSave={value => {
											setUsePm(value);
											finishEdit(finishEdit);
											getForecast(min, max, holding, end, false, value);
										}}
									/>
								)}
							</Descriptions.Item>
							{Recommendations.sdcEnabled && (
								<Descriptions.Item
									labelStyle={{fontWeight: "bold"}}
									label={"Exclude SDC Codes"}
									span={3}>
									{(!editingMode || editField !== "skip_sdc_code") && (
										<div>
											{skipSDC.join(", ")}
											<EditOutlined onClick={() => startEdit("skip_sdc_code")} />
										</div>
									)}
									{editingMode && editField === "skip_sdc_code" && (
										<Edit
											options={sdcCodes}
											defaultValue={skipSDC}
											_type={"tag-select"}
											onCancel={finishEdit}
											onSave={value => {
												setSkipSDC(value);
												finishEdit(finishEdit);
												getForecast(min, max, holding, end, false, usePm, value);
											}}
										/>
									)}
								</Descriptions.Item>
							)}
							<Descriptions.Item
								labelStyle={{fontWeight: "bold"}}
								label={"Use demand smoothing"}
								span={3}>
								{(!editingMode || editField !== "use_demand_smoothing") && (
									<div>
										{useSmoothing ? "Yes" : "No"}
										<EditOutlined onClick={() => startEdit("use_demand_smoothing")} />
									</div>
								)}
								{editingMode && editField === "use_demand_smoothing" && (
									<Edit
										options={selectSmoothingOptions}
										defaultValue={useSmoothing}
										_type={"select"}
										onCancel={finishEdit}
										onSave={value => {
											setUseSmoothing(value);
											finishEdit(finishEdit);
											getForecast(min, max, holding, end, false, usePm, skipSDC, value);
										}}
									/>
								)}
							</Descriptions.Item>
							<Descriptions.Item
								labelStyle={{fontWeight: "bold"}}
								label={`Coefficient for Demand Smoothing`}
								span={3}>
								{(!editingMode || editField !== "std_coeff") && (
									<div>
										{std_coeff} <EditOutlined onClick={() => startEdit("std_coeff")} />
									</div>
								)}
								{editingMode && editField === "std_coeff" && (
									<Edit
										defaultValue={std_coeff}
										_type={"numeric"}
										onCancel={finishEdit}
										onSave={value => {
											setStdCoeff(value);
											finishEdit();
											getForecast(
												min,
												max,
												holding,
												end,
												false,
												usePm,
												skipSDC,
												useSmoothing,
												value,
											);
										}}
									/>
								)}
							</Descriptions.Item>
							<Descriptions.Item labelStyle={{fontWeight: "bold"}} label={"Smoothing Mode"} span={3}>
								{(!editingMode || editField !== "smoothing_mode") && (
									<div>
										{labels[smoothingMethod]}
										<EditOutlined onClick={() => startEdit("smoothing_mode")} />
									</div>
								)}
								{editingMode && editField === "smoothing_mode" && (
									<Edit
										options={selectSmoothingMethodOptions}
										defaultValue={smoothingMethod}
										_type={"select"}
										onCancel={finishEdit}
										onSave={value => {
											setSmoothingMethod(value);
											finishEdit(finishEdit);
											getForecast(
												min,
												max,
												holding,
												end,
												false,
												usePm,
												skipSDC,
												useSmoothing,
												std_coeff,
												value,
											);
										}}
									/>
								)}
							</Descriptions.Item>
						</Descriptions>
					</Col>
					<Col span={14} style={{backgroundColor: "white", padding: 5}}>
						<ForecastingGraph
							data={graph}
							plannedData={plannedGraph}
							labels={["stock_level", "rop", "max"]}
							legendFields={["History Stock", "Reorder Point", "MAX"]}
							colors={["blue", "#00ff8033", "#ff00004d", "#00D30966"]}
						/>
					</Col>
				</Row>
			</Spin>
			<ModalWindow
				title={`Save new recommendation`}
				isModalVisible={isModalVisible}
				onOk={() => save()}
				scrollVisible={false}
				onCancel={() => setIsModalVisible(false)}>
				<StandardForm form={form} initialValues={{}} onFinish={onOk} fields={getValuesFields()} />
			</ModalWindow>
		</div>
	);
}
